import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as contentListActions from '../../actions/contentListActions';
import * as contentActions from '../../actions/contentActions';
import * as diagramActions from '../../actions/diagramActions';
import * as sessionActions from '../../actions/sessionActions';
import { createContentAccess } from '../../utils/accessLevelCheck';
import PageContainer from '../Global/Containers/PageContainer';
import MaxWidthContainer from '../Global/Containers/MaxWidthContainer';
import TopLevelHero from '../Global/Heros/TopLevelHero';
import MyContentList from './MyContentList';
import MainContentContainer from '../Global/Containers/MainContentContainer';
import DeleteContentModal from '../Global/Modals/DeleteContentModal';
import DeleteContentConflictModal from '../Global/Modals/DeleteContentConflictModal';
import LoadMoreButton from '../Global/Buttons/LoadMoreButton';
import UpsellModal from '../Global/Modals/UpsellModal';

class CreatePage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      loadingMore: false,
      contentTypeToDelete: ``,
      contentIdToDelete: 0,
      deleteContentModalOpen: false,
      deleteContentModalLoading: false,
      contentConflicts: [],
      conflictModalOpen: false,
      conflictModalLoading: false,
      upsellModalOpen: false,
      upsellModalFeature: `diagram`,
    };
  }

  componentWillReceiveProps(nextProps) {
    // If the user isn't logged in then hide the loader icon
    if (this.props.loggedInUser.notLoggedIn !== nextProps.loggedInUser.notLoggedIn) {
      this.setState({ loading: false });
    }

    // If we have loaded content
    if (this.props.content.status !== nextProps.content.status) {
      this.setState({ loading: false });
    }

    // If we have deleted a diagram which had content conflicts
    if (this.props.content.data.length !== nextProps.content.data.length) {
      this.setState({
        deleteContentModalOpen: false,
        deleteContentModalLoading: false,
        conflictModalLoading: false,
        conflictModalOpen: false,
      });
    }

    if (this.props.loggedInUser.id !== nextProps.loggedInUser.id) {
      this.props.actions.loadContentList(20, ``);
    }

    // If we have loaded more results
    if (this.props.content.cursor !== nextProps.content.cursor) {
      this.setState({ loadingMore: false });
    }
  }

  handleDeleteClick = (contentType, contentId) => {
    this.setState({ contentTypeToDelete: contentType, contentIdToDelete: contentId, deleteContentModalOpen: true });
  };

  deleteContent = (e) => {
    e.preventDefault();
    this.setState({ deleteContentModalLoading: true });
    switch (this.state.contentTypeToDelete) {
      case `pitchdiagram`:
        return this.props.actions.deleteDiagram(this.state.contentIdToDelete)
          // If when deleting a diagram we find there are drills that include it
          .catch(error => error.json().then((res) => {
            this.setState({ deleteContentModalOpen: false, deleteContentModalLoading: false, contentConflicts: res.data, conflictModalOpen: true });
          }));
      case `session`:
        return this.props.actions.deleteSession(this.state.contentIdToDelete);
      default:
        return this.props.actions.deleteContent(this.state.contentIdToDelete)
          // If when deleting content if we find there are sessions that include it
          .catch(error => error.json().then((res) => {
            this.setState({ deleteContentModalOpen: false, deleteContentModalLoading: false, contentConflicts: res.data, conflictModalOpen: true });
          }));
    }
  }

  forceDeleteContent = (e) => {
    e.preventDefault();
    this.setState({ conflictModalLoading: true });
    switch (this.state.contentTypeToDelete) {
      case `pitchdiagram`:
        this.props.actions.deleteDiagram(this.state.contentIdToDelete, true);
        break;
      default:
        this.props.actions.deleteContent(this.state.contentIdToDelete, true);
    }
  }

  closeModal = (modal) => {
    this.setState({ [modal]: false });
  }

  loadMoreClick = () => {
    this.setState({ loadingMore: true });
    this.props.actions.loadContentList(20, this.props.content.cursor, true);
  }

  handleCreateClick = (upsellType, href) => {
    if (this.props.loggedInUser.computed_access) {
      const canAccess = createContentAccess(this.props.loggedInUser.computed_access, this.props.loggedInUser.org_id);
      if (canAccess === false) {
        // Logged in but don't have access to the feature
        this.showUpsellModal(upsellType);
      } else {
        window.location.href = href;
      }
    } else {
      // Logged out users
      this.showUpsellModal(upsellType);
    }
  }

  showUpsellModal = (upsellType) => {
    this.setState({ upsellModalOpen: true, upsellModalFeature: upsellType });
  }

  render() {
    const { loggedInUser, content } = this.props;
    const { loading, loadingMore, contentTypeToDelete, deleteContentModalOpen, deleteContentModalLoading, contentConflicts, conflictModalOpen, conflictModalLoading, upsellModalOpen, upsellModalFeature } = this.state;

    return (
      <div id="create-page">
        <PageContainer classes="bg-white">
          <TopLevelHero pageId="my-content" handleCreateClick={this.handleCreateClick} />

          <MaxWidthContainer>
            <MainContentContainer>
              <MyContentList
                myContent={content.data}
                onDelete={this.handleDeleteClick}
                loggedInUser={loggedInUser}
                moreResults={content.more}
                loading={loading}
              />
              <LoadMoreButton
                moreResults={content.more}
                loadMoreClick={this.loadMoreClick}
                loading={loadingMore}
                classes="border-radius-l"
              />
            </MainContentContainer>
          </MaxWidthContainer>

          <DeleteContentModal
            modalIsOpen={deleteContentModalOpen}
            loading={deleteContentModalLoading}
            onConfirmClick={this.deleteContent}
            onCloseClick={() => this.closeModal(`deleteContentModalOpen`)}
            contentType={contentTypeToDelete}
          />

          <DeleteContentConflictModal
            modalIsOpen={conflictModalOpen}
            loading={conflictModalLoading}
            contentConflicts={contentConflicts}
            onConfirmClick={this.forceDeleteContent}
            onCloseClick={() => this.closeModal(`conflictModalOpen`)}
            contentType={contentTypeToDelete}
          />

          <UpsellModal
            loggedInUser={loggedInUser}
            modalIsOpen={upsellModalOpen}
            upsellFeature={upsellModalFeature}
            onCloseClick={() => this.closeModal(`upsellModalOpen`)}
          />
        </PageContainer>
      </div>
    );
  }
}

CreatePage.propTypes = {
  loggedInUser: PropTypes.object.isRequired,
  content: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    loggedInUser: state.loggedInUser,
    content: state.contentList,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Object.assign({}, contentListActions, contentActions, diagramActions, sessionActions), dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CreatePage);



// WEBPACK FOOTER //
// ./library/app/components/Create/CreatePage.js